type StoryfirstUnitName =
  | 'Story'
  | 'Moment'
  | 'Content'
  | 'Stories'
  | 'Moments'
  | 'Contents'
export type StoryfirstUnitValue =
  | 'story'
  | 'moment'
  | 'content'
  | 'stories'
  | 'moments'
  | 'contents'

/**
 * Standard hierarchy for organizing user-generated content.
 *
 * @note Use a helper (like the `storyfirstUnitDisplayName` function or
 *   `storyfirstUnits` tagged template) instead of rendering this object’s
 *   values directly in the UI.
 */
export const StoryfirstUnit: Record<StoryfirstUnitName, StoryfirstUnitValue> = {
  /**
   * A `Story` has 0 or more `Moments`, and is the top of the hierarchy.
   */
  Story: 'story',
  /**
   * A `Moment` belongs to 0 or more `Stories`, and has 0 or more `Contents`.
   * The first `Story` a `Moment` belongs to is referred to as the
   * ‘primary story’, and controls metadata like the `Moment`’s inherited label
   * sets. Any additional `Stories` the `Moment` belongs to are ‘secondary
   * stories’.
   */
  Moment: 'moment',
  /**
   * `Content` belongs to a single `Moment`, and is the bottom of the
   * hierarchy.
   */
  Content: 'content',

  /** Plural of `Story`. */
  Stories: 'stories',
  /** Plural of `Moment`. */
  Moments: 'moments',
  /**
   * Plural of `Content`. Note that we render this as ‘pieces of content’ in
   * the UI because ‘5 contents’ is awkward, but the unit is named `Contents`
   * to match the naming pattern for `Story` → `Stories` and
   * `Moment` → `Moments`.
   */
  Contents: 'contents',
} as const

/**
 * The Storyfirst unit above `unit` in the hierarchy, if one exists. Always
 * returns the singular form of the parent unit.
 *
 * @example
 * storyfirstParentUnit(StoryfirstUnit.Story)
 * //=> undefined
 * storyfirstParentUnit(StoryfirstUnit.Moments)
 * //=> StoryfirstUnit.Story
 * storyfirstParentUnit(StoryfirstUnit.Content)
 * //=> StoryfirstUnit.Moment
 */
export const storyfirstParentUnit = (
  unit: StoryfirstUnitValue
): StoryfirstUnitValue | undefined => {
  // note that this exploits case fallthrough
  switch (unit) {
    case StoryfirstUnit.Moment:
    case StoryfirstUnit.Moments:
      return StoryfirstUnit.Story
    case StoryfirstUnit.Content:
    case StoryfirstUnit.Contents:
      return StoryfirstUnit.Moment
    default:
      return
  }
}

const plurals: StoryfirstUnitValue[] = [
  StoryfirstUnit.Stories,
  StoryfirstUnit.Moments,
  StoryfirstUnit.Contents,
]

/**
 * The UI ‘presentational’ version of Storyfirst unit names.
 *
 * Currently unit names can only have one presentational form (for example,
 * `StoryfirstUnit.Story` → `"story"`), but we’ve discussed allowing customers
 * to change the names (for example, ‘campaign’ instead of ‘story’) to better
 * match the customer’s vocabulary.
 *
 * Note that if `unit` is plural (`Stories`, `Moments`, `Contents`) the
 * returned text will always be plural, even if `plural` is `false`. This
 * simplifies the implementation of `storyfirstUnits`.
 *
 * @example
 * storyfirstUnitDisplayName(StoryfirstUnit.Story)
 * //=> "story"
 * storyfirstUnitDisplayName(StoryfirstUnit.Story, true)
 * //=> "stories"
 * storyfirstUnitDisplayName(StoryfirstUnit.Stories)
 * //=> "stories"
 */
export const storyfirstUnitDisplayName = (
  unit: StoryfirstUnitValue,
  plural = false
): string | undefined => {
  if (!plural && !plurals.includes(unit)) return unit

  // note that this exploits case fallthrough
  switch (unit) {
    case StoryfirstUnit.Story:
    case StoryfirstUnit.Stories:
      return 'stories'

    case StoryfirstUnit.Moment:
    case StoryfirstUnit.Moments:
      return 'moments'

    case StoryfirstUnit.Content:
    case StoryfirstUnit.Contents:
      return 'pieces of content'

    default:
      return
  }
}

/**
 * Tagged template for easily embedding ‘presentational’ Storyfirst unit names
 * in text. A convenience wrapper around `storyfirstUnitDisplayName`.
 *
 * @example
 * storyfirstUnits`${amount} remaining in ${StoryfirstUnit.Story} budget`
 * //=> "$100 remaining in story budget"
 * storyfirstUnits`${count} ${StoryfirstUnit.Contents} in this ${StoryfirstUnit.Moment}`
 * //=> "5 pieces of content in this moment"
 */
export const storyfirstUnits = (
  strings: TemplateStringsArray,
  ...embeds: Array<StoryfirstUnitValue | string>
): string => {
  return strings.reduce((memo, string, index) => {
    memo += string

    const embed = embeds[index]
    if (embed === undefined) return memo

    let unit: StoryfirstUnitValue | null = null
    if (Object.values(StoryfirstUnit).includes(embed as StoryfirstUnitValue)) {
      unit = embed as StoryfirstUnitValue
    }

    if (unit) memo += storyfirstUnitDisplayName(unit)
    else memo += embed

    return memo
  }, '')
}
